// App.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import io from 'socket.io-client';
import { v4 as uuidv4 } from 'uuid';
import './index.css'; // Global CSS

// Import modularized components
import LoginScreen from './components/LoginScreen';
import Sidebar from './components/Sidebar';
import GameTab from './components/GameTab';
import ChallengeAdminTab from './components/ChallengeAdminTab';
import SettingsTab from './components/SettingsTab';
import DrawnChallengeModal from './components/DrawnChallengeModal';

// Initialize Socket.IO connection
const socket = io('https://painbook.easy-dev.de:5001');

// Helper function to get or generate user ID
const getUserId = () => {
  let userId = localStorage.getItem('userId');
  if (!userId) {
    userId = uuidv4();
    localStorage.setItem('userId', userId);
  }
  return userId;
};

// Main application component
function App() {
  // --- Core States ---
  const [username, setUsername] = useState('');
  const [roomId, setRoomId] = useState('');
  const [inRoom, setInRoom] = useState(false);
  const [users, setUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [hostId, setHostId] = useState('');
  const [challengePool, setChallengePool] = useState([]);
  const [selectedChallenges, setSelectedChallenges] = useState([]);
  const [drawnChallenges, setDrawnChallenges] = useState([]);
  const [timer, setTimer] = useState(180);
  const [chatMessages, setChatMessages] = useState([]);
  const [roleAssignments, setRoleAssignments] = useState([]);
  const [chatInput, setChatInput] = useState(''); // State for chat input

  // --- Game Configuration States ---
  const [isRunning, setIsRunning] = useState(false);
  const [paused, setPaused] = useState(false);
  const [allowDuplicates, setAllowDuplicates] = useState(false);
  const [separateEffectDrawing, setSeparateEffectDrawing] = useState(false);
  const [deleteIfExpired, setDeleteIfExpired] = useState(false);
  const [penaltySettings, setPenaltySettings] = useState({
    maxPenaltyPoints: 3,
    penaltyDescription: '',
  });

  // --- UI States ---
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    if (savedMode !== null) {
      return savedMode === 'true';
    }
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const [activeTab, setActiveTab] = useState('game'); // 'game', 'challenge-admin', 'settings'
  const [currentChallengeModal, setCurrentChallengeModal] = useState(null); // For displaying drawn challenge in a modal

  // --- Refs for Auto-Scrolling (still managed here as they are cross-component) ---
  const positiveChallengesRef = useRef(null);
  const negativeChallengesRef = useRef(null);
  const chatBoxRef = useRef(null);

  // --- useEffects for Socket.IO and Initialization ---
  useEffect(() => {
    const userId = getUserId();
    socket.auth = { userId };
    socket.connect();

    socket.on('roomData', (data) => {
      console.log('Received roomData:', JSON.stringify(data, null, 2));
      setRoomId(data.id);
      setUsername(data.username);
      setUsers(data.users);
      setAdmins(data.admins);
      setHostId(data.host);
      setTimer(data.timer);
      setIsRunning(data.isRunning);
      setPaused(data.paused);
      setAllowDuplicates(data.allowDuplicates);
      setSeparateEffectDrawing(data.separateEffectDrawing);
      setDeleteIfExpired(data.deleteIfExpired || false);
      setSelectedChallenges(data.selectedChallenges);
      setDrawnChallenges(data.drawnChallenges);
      setAuditLog(data.auditLog);
      setRoleAssignments(data.roleAssignments || []);
      setChatMessages(data.chatLog || []);
      setInRoom(true);
      setPenaltySettings({
        maxPenaltyPoints: data.maxPenaltyPoints,
        penaltyDescription: data.penaltyDescription
      });
    });

    socket.on('newChatMessage', (msg) => {
      setChatMessages(prev => [...prev, msg]);
    });

    socket.on('connect_error', (err) => {
      console.error(`connect_error due to ${err.message}`);
    });

    socket.on('error', message => {
      alert(`Fehler: ${message}`);
    });

    socket.on('disconnect', () => {
      console.log('Disconnected from server.');
      // Optional: setInRoom(false) if app should reset on disconnect
    });

    socket.on('challengePoolUpdated', (pool) => {
      console.log('Challenge Pool updated:', JSON.stringify(pool, null, 2));
      setChallengePool(pool);
    });

    socket.on('showDrawnChallengeModal', (challenge) => {
      setCurrentChallengeModal(challenge);
    });

    return () => {
      socket.off('roomData');
      socket.off('connect_error');
      socket.off('disconnect');
      socket.off('challengePoolUpdated');
      socket.off('showDrawnChallengeModal');
      socket.off('newChatMessage');
    };
  }, []);

  // Effect for Dark Mode persistence
  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
    localStorage.setItem('darkMode', isDarkMode);
  }, [isDarkMode]);

  // Effect for auto-scrolling drawn challenges (kept here as refs are in App)
  useEffect(() => {
    if (positiveChallengesRef.current) {
      positiveChallengesRef.current.scrollTop = positiveChallengesRef.current.scrollHeight;
    }
    if (negativeChallengesRef.current) {
      negativeChallengesRef.current.scrollTop = negativeChallengesRef.current.scrollHeight;
    }
  }, [drawnChallenges]);

  // Effect for auto-scrolling chat (kept here as refs are in App)
  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // --- Socket.IO Event Emitters (Wrapped in useCallback where applicable) ---
  // These are passed down to child components
  const joinRoom = useCallback(() => {
    if (username && roomId) {
      socket.emit('joinRoom', { roomId, username, userId: getUserId() });
    }
  }, [username, roomId]);

  const createRoom = useCallback(() => {
    if (username) {
      socket.emit('createRoom', { username, userId: getUserId() });
    }
  }, [username]);

  const leaveRoom = useCallback(() => {
    socket.emit('leaveRoom', {});
    setInRoom(false);
    setRoomId('');
    setUsername('');
    setUsers([]);
    setAdmins([]);
    setHostId('');
    setSelectedChallenges([]);
    setDrawnChallenges([]);
    setAuditLog([]);
    setIsRunning(false);
    setPaused(false);
    setAllowDuplicates(false);
    setSeparateEffectDrawing(false);
    setActiveTab('game');
  }, []);

  const startGame = useCallback(() => { socket.emit('startGame', { roomId }); }, [roomId]);
  const pauseTimer = useCallback(() => { socket.emit('pauseTimer', { roomId }); }, [roomId]);
  const resumeTimer = useCallback(() => { socket.emit('resumeTimer', { roomId }); }, [roomId]);

  const resetRoom = useCallback(() => {
    if (window.confirm('Soll der Raum wirklich zurückgesetzt werden? Der Timer und alle gezogenen Challenges werden gelöscht.')) {
      socket.emit('resetRoom', { roomId });
    }
  }, [roomId]);

  const setGameTimer = useCallback((newTimer) => {
    socket.emit('setTimer', { roomId, seconds: newTimer });
  }, [roomId]);

  const toggleAllowDuplicates = useCallback(() => { socket.emit('setAllowDuplicates', { roomId, value: !allowDuplicates }); }, [roomId, allowDuplicates]);
  const toggleSeparateEffectDrawing = useCallback(() => { socket.emit('setSeparateEffectDrawing', { roomId, value: !separateEffectDrawing }); }, [roomId, separateEffectDrawing]);
  const toggleDeleteIfExpired = useCallback(() => {
    const newValue = !deleteIfExpired;
    setDeleteIfExpired(newValue);
    socket.emit('setDeleteIfExpired', { roomId, value: newValue });
  }, [roomId, deleteIfExpired]);

  const onRemoveDrawnChallenge = useCallback((drawnId) => {
    socket.emit('removeDrawnChallenge', { roomId: roomId, drawnId: drawnId });
  }, [roomId]);

  const kickPlayer = useCallback((id) => {
    if (window.confirm("Wirklich kicken?")) {
      socket.emit('kickPlayer', { roomId, playerId: id });
    }
  }, [roomId]);

  const toggleAdmin = useCallback((id, name) => {
    const isCurrentlyAdmin = admins.includes(id);
    const confirmText = isCurrentlyAdmin
      ? `Admin-Rechte von ${name} entfernen?`
      : `${name} zum Admin machen?`;
    if (window.confirm(confirmText)) {
      socket.emit('setAdmin', { roomId, playerId: id, value: !isCurrentlyAdmin });
    }
  }, [roomId, admins]);

  const toggleChallengeSelection = useCallback((cat, id) => {
    const exists = selectedChallenges.some(sel => sel.category === cat && sel.id === id);

    if (exists) {
      const updated = selectedChallenges.filter(sel => !(sel.category === cat && sel.id === id));
      setSelectedChallenges(updated);
      socket.emit('setSelectedChallenges', { roomId, challenges: updated });
    } else {
      const categoryObj = challengePool.find(c => c.category === cat);
      const challenge = categoryObj?.challenges.find(ch => ch.id === id);
      const weight = challenge?.weight || 1;
      const newEntries = Array.from({ length: weight }, () => ({ category: cat, id }));
      const updated = [...selectedChallenges, ...newEntries];
      setSelectedChallenges(updated);
      socket.emit('setSelectedChallenges', { roomId, challenges: updated });
    }
  }, [roomId, selectedChallenges, challengePool]);

  const toggleCategorySelection = useCallback((cat) => {
    const catChallenges = challengePool.find(c => c.category === cat)?.challenges || [];
    const allSelected = catChallenges.every(ch =>
      selectedChallenges.filter(sel => sel.category === cat && sel.id === ch.id).length === (ch.weight || 1)
    );

    if (allSelected) {
      const updated = selectedChallenges.filter(sel => sel.category !== cat);
      setSelectedChallenges(updated);
      socket.emit('setSelectedChallenges', { roomId, challenges: updated });
    } else {
      const additions = catChallenges.flatMap(ch =>
        Array.from({ length: ch.weight || 1 }, () => ({ category: cat, id: ch.id }))
      );
      const updated = [
        ...selectedChallenges.filter(sel => sel.category !== cat),
        ...additions
      ];
      setSelectedChallenges(updated);
      socket.emit('setSelectedChallenges', { roomId, challenges: updated });
    }
  }, [roomId, selectedChallenges, challengePool]);

  const addCategory = useCallback((categoryName) => {
    socket.emit('addCategory', { categoryName });
  }, []);

  const renameCategory = useCallback((oldName, newName) => {
    socket.emit('renameCategory', { oldName, newName });
  }, []);

  const deleteCategory = useCallback((categoryName) => {
    socket.emit('removeCategory', { categoryName });
  }, []);

  const addOrUpdateChallenge = useCallback((originalCategory, category, challengeData) => {
    if (challengeData.id) { // This means it's an existing challenge being edited
      socket.emit('editChallenge', {
        originalCategory,
        category,
        challenge: { ...challengeData }
      });
    } else { // This is a new challenge
      socket.emit('addChallenge', { category, challenge: challengeData });
    }
  }, []);

  const deleteChallenge = useCallback((category, challengeId) => {
    socket.emit('removeChallenge', { category, challengeId });
  }, []);

  const sendChatMessage = useCallback((message) => {
    if (message.trim()) { // Ensure message is not empty
      socket.emit('sendChatMessage', { roomId, message });
      setChatInput(''); // Clear the input after sending
    }
  }, [roomId, setChatInput]);

  const deleteChatMessage = useCallback((messageId) => {
    if (window.confirm('Nachricht wirklich löschen?')) {
      socket.emit('deleteChatMessage', { roomId, messageId });
    }
  }, [roomId]);

  const assignRoles = useCallback(() => {
    socket.emit('assignRoles', { roomId });
  }, [roomId]);

  const givePenalty = useCallback((targetUserId) => {
    if (!roomId) return;
    socket.emit('givePenalty', {
      roomId,
      targetUserId: targetUserId
    });
  }, [roomId]);

  const updatePenaltySettings = useCallback((maxPenaltyPoints, penaltyDescription) => {
    socket.emit('updatePenaltySettings', {
      roomId,
      maxPenaltyPoints,
      penaltyDescription,
    });
  }, [roomId]);

  // --- Helper Logic for UI Rendering ---
  const userId = getUserId();
  const isHost = userId === hostId;
  const isAdmin = isHost || admins.includes(userId);
  const canManageChallenges = isHost || isAdmin;

  const isExpired = useCallback((challenge) => {
    return typeof challenge.remainingPhases === 'number' && challenge.remainingPhases <= 0;
  }, []);

  return (
    <div className={`min-h-screen flex flex-col ${isDarkMode ? 'dark' : ''}`}>
      {!inRoom ? (
        <LoginScreen
          username={username}
          setUsername={setUsername}
          roomId={roomId}
          setRoomId={setRoomId}
          createRoom={createRoom}
          joinRoom={joinRoom}
          isDarkMode={isDarkMode}
          setIsDarkMode={setIsDarkMode}
        />
      ) : (
        <div className="flex-grow flex flex-col md:flex-row bg-background text-text">
          <Sidebar
            roomId={roomId}
            users={users}
            hostId={hostId}
            admins={admins}
            userId={userId}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            canManageChallenges={canManageChallenges}
            leaveRoom={leaveRoom}
            isDarkMode={isDarkMode}
            setIsDarkMode={setIsDarkMode}
          />
          <main className="flex-grow p-6 overflow-y-auto">
            {activeTab === 'game' && (
              <GameTab
                roomId={roomId}
                timer={timer}
                isRunning={isRunning}
                paused={paused}
                drawnChallenges={drawnChallenges}
                auditLog={auditLog}
                roleAssignments={roleAssignments}
                chatMessages={chatMessages}
                chatInput={chatInput} // Passed down for chat input state
                setChatInput={setChatInput} // Passed down to update chat input state
                positiveChallengesRef={positiveChallengesRef}
                negativeChallengesRef={negativeChallengesRef}
                chatBoxRef={chatBoxRef}
                isHost={isHost}
                isAdmin={isAdmin}
                users={users}
                penaltySettings={penaltySettings}
                startGame={startGame}
                pauseTimer={pauseTimer}
                resumeTimer={resumeTimer}
                resetRoom={resetRoom}
                onRemoveDrawnChallenge={onRemoveDrawnChallenge}
                sendChatMessage={sendChatMessage}
                deleteChatMessage={deleteChatMessage}
                assignRoles={assignRoles}
                givePenalty={givePenalty}
                isExpired={isExpired}
              />
            )}
            {activeTab === 'challenge-admin' && (
              <ChallengeAdminTab
                isAdmin={isAdmin}
                challengePool={challengePool}
                selectedChallenges={selectedChallenges}
                toggleChallengeSelection={toggleChallengeSelection}
                toggleCategorySelection={toggleCategorySelection}
                addCategory={addCategory}
                renameCategory={renameCategory}
                deleteCategory={deleteCategory}
                addOrUpdateChallenge={addOrUpdateChallenge}
                deleteChallenge={deleteChallenge}
              />
            )}
            {activeTab === 'settings' && (
              <SettingsTab
                isHost={isHost}
                isAdmin={isAdmin}
                users={users}
                hostId={hostId}
                admins={admins}
                userId={userId}
                allowDuplicates={allowDuplicates}
                separateEffectDrawing={separateEffectDrawing}
                deleteIfExpired={deleteIfExpired}
                timer={timer}
                penaltySettings={penaltySettings}
                toggleAllowDuplicates={toggleAllowDuplicates}
                toggleSeparateEffectDrawing={toggleSeparateEffectDrawing}
                toggleDeleteIfExpired={toggleDeleteIfExpired}
                setGameTimer={setGameTimer}
                kickPlayer={kickPlayer}
                toggleAdmin={toggleAdmin}
                updatePenaltySettings={updatePenaltySettings}
                setPenaltySettings={setPenaltySettings} // Passed down to allow child to update local state
              />
            )}
          </main>
        </div>
      )}
      <DrawnChallengeModal
        challenge={currentChallengeModal}
        onClose={() => setCurrentChallengeModal(null)}
      />
    </div>
  );
}

export default App;